import React, { Component } from 'react'
import PropTypes from 'prop-types'

import { toast } from '@fielded/shared-ui/src/components/Page'

import withConfig from '../van-shared/hoc/withConfig'
import ForgotPasswordApi from './forgot-password-api'
import ForgotPassword from './ForgotPassword'
import OTPVerification from './OTPVerification'
import ChangePassword from './ChangePassword'

class ForgotPasswordContainer extends Component {
  static propTypes = {
    resetLoginStatus: PropTypes.func,
    location: PropTypes.object.isRequired,
    history: PropTypes.object.isRequired,
    login: PropTypes.func,
    error: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    config: PropTypes.object.isRequired
  }

  static defaultProps = {
    error: null
  }

  state = {
    currentStep: 'method-selection',
    selectedMethod: null,
    contactData: null,
    maskedContact: null,
    resendTimer: 0,
    resendInterval: null,
    inProcess: false,
    error: null
  }

  componentDidMount () {
    const { config } = this.props
    this.api = new ForgotPasswordApi({ agaveApiUrl: config.agaveApiUrl })
  }

  componentWillUnmount () {
    if (this.state.resendInterval) {
      clearInterval(this.state.resendInterval)
    }
  }

  handleMethodSelect = (method) => {
    const backendMethod = method === 'phone' ? 'sms' : method

    this.setState({
      selectedMethod: backendMethod,
      currentStep: method === 'email' ? 'email-input' : 'phone-input',
      error: null
    })
  }

  handleSubmit = async (fields) => {
    const { currentStep, selectedMethod } = this.state

    try {
      this.setState({ inProcess: true, error: null })

      if (currentStep === 'email-input' || currentStep === 'phone-input') {
        const requestData = {
          ...fields,
          method: selectedMethod
        }

        await this.api.submit(requestData)

        toast({
          title: 'OTP Sent Successfully',
          type: 'success',
          children: `A verification code has been sent to your ${selectedMethod === 'email' ? 'email' : 'phone number'}`
        })

        this.setState({
          currentStep: 'otp-verification',
          contactData: fields,
          maskedContact: this.maskContact(selectedMethod === 'email' ? fields.email : fields.phoneNumber, selectedMethod),
          inProcess: false
        })

        this.startResendTimer()
      } else if (currentStep === 'otp-verification') {
        const verifyData = {
          ...this.state.contactData,
          otpCode: fields.otpCode,
          method: selectedMethod
        }

        await this.api.verifyOTP(verifyData)

        toast({
          title: 'OTP Verified Successfully',
          type: 'success',
          children: 'Your verification code has been confirmed. You can now set a new password.'
        })

        this.setState({
          currentStep: 'set-password',
          inProcess: false
        })
      } else if (currentStep === 'set-password') {
        if (fields.password !== fields.confirmPassword) {
          throw new Error('Passwords do not match')
        }

        const passwordData = {
          ...this.state.contactData,
          password: fields.password,
          confirmPassword: fields.confirmPassword,
          method: selectedMethod
        }

        await this.api.changePassword(passwordData)

        toast({
          title: 'Password Changed Successfully',
          type: 'success',
          children: 'Your password has been updated. You can now log in with your new password.'
        })

        this.props.history.push('/login')
      }
    } catch (err) {
      console.error('Error in forgot password flow:', err)
      this.setState({
        error: err.message || 'An error occurred. Please try again.',
        inProcess: false
      })
    }
  }

  handleResendOTP = async () => {
    if (this.state.resendTimer > 0) return

    try {
      this.setState({ inProcess: true })

      const resendData = {
        ...this.state.contactData,
        method: this.state.selectedMethod
      }

      await this.api.resendOTP(resendData)

      toast({
        title: 'OTP Resent Successfully',
        type: 'success',
        children: 'A new verification code has been sent to your contact method.'
      })

      this.setState({ inProcess: false })
      this.startResendTimer()
    } catch (err) {
      console.error('Error resending OTP:', err)
      this.setState({
        error: err.message || 'Failed to resend code. Please try again.',
        inProcess: false
      })
    }
  }

  startResendTimer = () => {
    this.setState({ resendTimer: 30 })

    const interval = setInterval(() => {
      this.setState(prevState => {
        if (prevState.resendTimer <= 1) {
          clearInterval(interval)
          return { resendTimer: 0, resendInterval: null }
        }
        return { resendTimer: prevState.resendTimer - 1 }
      })
    }, 1000)

    this.setState({ resendInterval: interval })
  }

  maskContact = (contact, method) => {
    if (method === 'email') {
      const [username, domain] = contact.split('@')
      if (username.length <= 2) {
        return `${username[0]}***@${domain}`
      }
      const maskedUsername = username.substring(0, 2) + '*'.repeat(username.length - 2)
      return `${maskedUsername}@${domain}`
    } else {
      const cleanNumber = contact.replace(/[^\d+]/g, '')
      if (cleanNumber.length <= 4) {
        return '*** *** *** ' + cleanNumber
      }
      const lastFour = cleanNumber.slice(-4)
      return `*** *** *** ${lastFour}`
    }
  }

  handleBack = () => {
    const { currentStep, selectedMethod } = this.state

    if (currentStep === 'email-input' || currentStep === 'phone-input') {
      this.setState({
        currentStep: 'method-selection',
        selectedMethod: null,
        error: null
      })
    } else if (currentStep === 'otp-verification') {
      this.setState({
        currentStep: selectedMethod === 'email' ? 'email-input' : 'phone-input',
        error: null,
        maskedContact: null
      })

      if (this.state.resendInterval) {
        clearInterval(this.state.resendInterval)
        this.setState({ resendTimer: 0, resendInterval: null })
      }
    } else if (currentStep === 'set-password') {
      this.setState({
        currentStep: 'otp-verification',
        error: null
      })
    }
  }

  render () {
    const {
      currentStep,
      inProcess,
      error,
      maskedContact,
      resendTimer
    } = this.state

    if (currentStep === 'otp-verification') {
      return (
        <OTPVerification
          config={this.props.config}
          maskedContact={maskedContact}
          onSubmit={this.handleSubmit}
          onResendOTP={this.handleResendOTP}
          onBack={this.handleBack}
          inProcess={inProcess}
          error={error}
          resendTimer={resendTimer}
        />
      )
    }

    if (currentStep === 'set-password') {
      return (
        <ChangePassword
          config={this.props.config}
          onSubmit={this.handleSubmit}
          onBack={this.handleBack}
          inProcess={inProcess}
          error={error}
        />
      )
    }

    return (
      <ForgotPassword
        config={this.props.config}
        currentStep={currentStep}
        onMethodSelect={this.handleMethodSelect}
        onSubmit={this.handleSubmit}
        inProcess={inProcess}
        error={error}
        onBack={this.handleBack}
      />
    )
  }
}

export default withConfig(ForgotPasswordContainer)
